/**
 * @fileoverview Modal de confirmação pré-disparo do disparo em massa durável.
 * Resume bases selecionadas, total estimado de válidos, limite diário, dias/
 * horários, conexão, template e duração aproximada. Roda POST /mass-send/validate
 * (via prop) antes de liberar o botão — se inválido, mostra os erros e bloqueia.
 * Presentacional: recebe o resultado da validação e o callback de confirmação.
 */

import React from "react";
import { Dialog, CircularProgress } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import {
  Send, X, AlertTriangle, CheckCircle2, Calendar, Clock, Database, Users, Zap,
} from "lucide-react";

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const weekdaysLabel = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0 || arr.length === 7) return "Todos os dias";
  return arr.slice().sort((a, b) => a - b).map((d) => WEEKDAYS[d] || d).join(", ");
};

const Row = ({ icon, label, value, valueColor, C }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
    <span style={{ color: C.accent, display: "inline-flex", flexShrink: 0 }}>{icon}</span>
    <span style={{ flex: 1, fontSize: 12, color: C.faint }}>{label}</span>
    <span style={{ fontSize: 12, fontWeight: 600, color: valueColor || C.text, textAlign: "right" }}>{value}</span>
  </div>
);

const MassSendConfirmModal = ({
  open, onClose, onConfirm, dispatching,
  validating, validation,
  bases, connectionName, templateName, schedule,
}) => {
  const isDark = useTheme().palette.type === "dark";
  const W = (a) => (isDark ? `rgba(255,255,255,${a})` : `rgba(20,22,30,${a})`);
  const C = {
    bg: isDark ? "#0f1115" : "#ffffff",
    border: W(0.08),
    text: W(0.88),
    faint: W(0.5),
    accent: "#818cf8",
  };

  const sched = schedule || {};
  const dailyLimit = sched.maxSendsPerDay || 100;
  const baseList = bases || [];
  const sumValid = baseList.reduce((s, b) => s + ((b && b.validRows) || 0), 0);
  const estimated =
    validation && typeof validation.estimatedContacts === "number"
      ? validation.estimatedContacts
      : sumValid;
  const estimatedDays = dailyLimit > 0 ? Math.ceil((estimated || 0) / dailyLimit) : 1;
  const windowLabel =
    sched.windowStart && sched.windowEnd ? `${sched.windowStart} – ${sched.windowEnd}` : "Sem restrição";

  const errors = (validation && validation.errors) || [];
  const isValid = validation && validation.valid;
  const canConfirm = !validating && !dispatching && isValid;
  const sampleBody = validation && validation.sample && validation.sample.body;

  return (
    <Dialog
      open={open}
      onClose={dispatching ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { background: C.bg, color: C.text, borderRadius: 14, border: `1px solid ${C.border}` } }}
    >
      {/* Header */}
      <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(99,102,241,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Send size={16} color={C.accent} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Confirmar disparo em massa</div>
          <div style={{ fontSize: 11, color: C.faint }}>Revise os detalhes antes de iniciar</div>
        </div>
        <span onClick={dispatching ? undefined : onClose} style={{ cursor: dispatching ? "default" : "pointer", color: C.faint, padding: 4, opacity: dispatching ? 0.4 : 1 }}>
          <X size={18} />
        </span>
      </div>

      <div style={{ padding: "16px 20px", maxHeight: "70vh", overflowY: "auto" }}>
        {/* Bases selecionadas */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: C.faint, marginBottom: 8 }}>
            Bases selecionadas ({baseList.length})
          </div>
          {baseList.length === 0 ? (
            <div style={{ fontSize: 12, color: "#f59e0b" }}>Nenhuma base selecionada.</div>
          ) : (
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
              {baseList.map((b, i) => (
                <div
                  key={b.id != null ? b.id : i}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "7px 10px",
                    borderBottom: i < baseList.length - 1 ? `1px solid ${C.border}` : "none",
                  }}
                >
                  <span style={{ width: 18, height: 18, borderRadius: 5, background: "rgba(99,102,241,0.25)", color: "#c7d2fe", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {i + 1}
                  </span>
                  <span style={{ flex: 1, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {b.name || `Base #${b.id}`}
                  </span>
                  <span style={{ fontSize: 11, color: C.faint, display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <Users size={11} /> {((b && b.validRows) || 0).toLocaleString("pt-BR")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Resumo */}
        <div style={{ marginBottom: 4 }}>
          <Row
            icon={<Users size={14} />}
            label="Total estimado de válidos"
            value={validating ? "…" : (estimated || 0).toLocaleString("pt-BR")}
            valueColor="#34d399"
            C={C}
          />
          <Row icon={<Zap size={14} />} label="Limite diário" value={`${dailyLimit.toLocaleString("pt-BR")}/dia`} C={C} />
          <Row icon={<Calendar size={14} />} label="Dias de envio" value={weekdaysLabel(sched.weekdays)} C={C} />
          <Row icon={<Clock size={14} />} label="Horário" value={windowLabel} C={C} />
          <Row icon={<Calendar size={14} />} label="Duração aproximada" value={`~${estimatedDays} dia${estimatedDays === 1 ? "" : "s"}`} C={C} />
          <Row icon={<Send size={14} />} label="Conexão" value={connectionName || "—"} C={C} />
          <Row icon={<Database size={14} />} label="Template" value={templateName || "—"} C={C} />
        </div>

        {/* Estado da validação */}
        {validating && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, fontSize: 12, color: C.faint }}>
            <CircularProgress size={14} style={{ color: C.accent }} /> Validando configuração…
          </div>
        )}

        {!validating && errors.length > 0 && (
          <div style={{ marginTop: 14, padding: "10px 12px", borderRadius: 8, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#f87171", marginBottom: 6 }}>
              <AlertTriangle size={13} /> Corrija antes de disparar
            </div>
            {errors.map((e, i) => (
              <div key={i} style={{ fontSize: 11, color: W(0.75), lineHeight: 1.5 }}>
                • {typeof e === "string" ? e : e.message || JSON.stringify(e)}
              </div>
            ))}
          </div>
        )}

        {!validating && isValid && errors.length === 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 14, fontSize: 12, color: "#34d399" }}>
            <CheckCircle2 size={14} /> Configuração validada.
          </div>
        )}

        {/* Prévia da mensagem */}
        {!validating && sampleBody && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: C.faint, marginBottom: 6 }}>
              Prévia da mensagem
            </div>
            <div style={{ padding: "10px 12px", borderRadius: 8, background: W(0.03), border: `1px dashed ${C.border}`, fontSize: 12, lineHeight: 1.5, color: W(0.75), whiteSpace: "pre-wrap" }}>
              {sampleBody}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: "14px 20px", display: "flex", gap: 10, justifyContent: "flex-end", borderTop: `1px solid ${C.border}` }}>
        <button
          onClick={dispatching ? undefined : onClose}
          disabled={dispatching}
          style={{
            padding: "9px 18px", borderRadius: 9, border: `1px solid ${C.border}`, background: "transparent",
            color: C.faint, fontSize: 13, fontWeight: 600, cursor: dispatching ? "not-allowed" : "pointer",
          }}
        >
          Cancelar
        </button>
        <button
          onClick={() => { if (canConfirm) onConfirm(); }}
          disabled={!canConfirm}
          style={{
            padding: "9px 20px", borderRadius: 9, border: "none",
            background: canConfirm ? "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)" : W(0.08),
            color: canConfirm ? "#fff" : W(0.3), fontSize: 13, fontWeight: 500,
            cursor: canConfirm ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 8,
          }}
        >
          {dispatching ? <CircularProgress size={14} style={{ color: "#fff" }} /> : <Send size={14} />}
          {dispatching ? "Iniciando…" : "Confirmar e disparar"}
        </button>
      </div>
    </Dialog>
  );
};

export default MassSendConfirmModal;
